import { create } from 'zustand';
import { useEffect } from 'react';

type Theme = 'day' | 'night';
interface InitialState {
  haveAccount: boolean;
  setHaveAccount: (value: boolean) => void;
  isLoadingPage: boolean;
  setIsLoadingPage: (value: boolean) => void;
  dashboardTitle: string;
  setDashboardTitle: (value: string) => void;
  theme: Theme;
  setTheme: (value: Theme) => void;
  // Authentication state
  isAuthenticated: boolean;
  sessionId: string | null;
  userId: string | null;
  setAuthState: (sessionId: string | null, userId: string | null) => void;
  clearAuthState: () => void;
  initializeAuthState: () => void;
}

export const useStore = create<InitialState>((set, get) => ({
  haveAccount: false,
  setHaveAccount: (value) => set({ haveAccount: value }),
  isLoadingPage: false,
  setIsLoadingPage: (value) => set({ isLoadingPage: value }),
  dashboardTitle: '',
  setDashboardTitle: (value) => set({ dashboardTitle: value }),
  theme: 'day',
  setTheme: (value) => set({ theme: value }),
  // Authentication state
  isAuthenticated: false,
  sessionId: null,
  userId: null,
  setAuthState: (sessionId, userId) =>
    set({
      isAuthenticated: !!sessionId,
      sessionId,
      userId,
    }),
  clearAuthState: () =>
    set({
      isAuthenticated: false,
      sessionId: null,
      userId: null,
    }),
  initializeAuthState: () => {
    const sessionId = localStorage.getItem('sessionId');
    const userId = localStorage.getItem('userId');
    set({
      isAuthenticated: !!sessionId,
      sessionId,
      userId,
    });
  },
}));

// Custom hook to initialize auth state on app startup
export const useInitializeAuth = () => {
  const { initializeAuthState, clearAuthState } = useStore();

  useEffect(() => {
    initializeAuthState();

    // Listen for storage changes (when localStorage is cleared from other parts of the app)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sessionId' && e.newValue === null) {
        clearAuthState();
      }
    };

    // Listen for custom events (when localStorage is cleared programmatically)
    const handleCustomEvent = () => {
      clearAuthState();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStateCleared', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateCleared', handleCustomEvent);
    };
  }, [initializeAuthState, clearAuthState]);
};
